import { PurchaseOrderEditorComponent } from '../../work-orders/purchase-orders/purchase-order-editor.component';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { DateTime } from 'luxon';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';

import { ConfirmDialogComponent } from '../../confirm-dialog/confirm-dialog.component';
import { LadingNote } from '../../work-orders/work-order-details/work-order.interfaces';
import { STATUSES, TRAILER_TYPES } from '../../work-orders/select-options';
import { PANEL_SECTIONS } from './work-order-panel.navigation';
import { WorkOrderStopEditorComponent } from './stop-editor/work-order-stop-editor.component';
import { WorkOrderStopRole } from './stop-editor/work-order-stop.models';
import { freightFacts, panelContact, scheduleFacts } from './work-order-panel.context';
import {
  WorkOrderV2Row,
  WorkOrderV2StatusPayload,
  WorkOrderV2StatusTimelineInfo,
} from '../work-orders-v2.models';
import { WorkOrdersV2Service } from '../work-orders-v2.service';
import { WorkOrderPanelService } from './work-order-panel.service';
import {
  appointmentPatch,
  appointmentValidation,
  appointmentValues,
  freightPatch,
  freightValues,
  referencePatch,
  referenceValues,
  statusValidation,
} from './work-order-panel.helpers';
import {
  PanelDetails,
  PanelContact,
  PanelFact,
  WorkOrderPanelNotice,
  WorkOrderPanelSavedEvent,
  WorkOrderPanelSection,
  WorkOrderPanelTab,
  WorkOrderPanelTarget,
} from './work-order-panel.models';

@Component({
  selector: 'app-work-order-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, WorkOrderStopEditorComponent, PurchaseOrderEditorComponent],
  templateUrl: './work-order-panel.component.html',
  styleUrls: ['./work-order-panel.component.scss'],
})
export class WorkOrderPanelComponent implements OnDestroy {
  @Input() themeMode: 'dark' | 'light' = 'dark';
  @Output() saved = new EventEmitter<WorkOrderPanelSavedEvent>();
  @Output() closed = new EventEmitter<void>();
  @ViewChild(PurchaseOrderEditorComponent) private purchaseOrders?: PurchaseOrderEditorComponent;
  @ViewChild('closeButton') private closeButton?: ElementRef<HTMLButtonElement>;
  @ViewChild('panelBody') private panelBody?: ElementRef<HTMLElement>;
  @ViewChildren(WorkOrderStopEditorComponent) private stopEditors?: QueryList<WorkOrderStopEditorComponent>;

  row: WorkOrderV2Row | null = null;
  details: PanelDetails | null = null;
  activeTab: WorkOrderPanelTab = 'overview';
  loading = false;
  loadError = '';
  savingSection: WorkOrderPanelSection | null = null;
  notice: WorkOrderPanelNotice | null = null;
  notes: LadingNote[] = [];
  noteDraft = '';
  editingNote: LadingNote | undefined;
  statusHistory: WorkOrderV2StatusTimelineInfo[] = [];
  routeContacts: PanelContact[] = [];
  partyContacts: PanelContact[] = [];
  freightSummary: PanelFact[] = [];
  plannedTimes: PanelFact[] = [];

  readonly statuses = STATUSES;
  readonly sections = PANEL_SECTIONS;
  readonly stopRoles: readonly WorkOrderStopRole[] = ['shipper', 'consignee'];
  readonly equipmentTypes = Object.entries(TRAILER_TYPES).map(([code, name]) => ({ code, name }));
  readonly browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  readonly referenceForm = this.forms.nonNullable.group({
    pickup_reference_number: '',
    delivery_reference_number: '',
    booking_number: '',
    release_number: '',
  });
  readonly freightForm = this.forms.nonNullable.group({
    type_of_equipment: '',
    carrier_trailer: '',
    carrier_tractor: '',
    seal_number: '',
    load_details: '',
    load_special_info: '',
  });
  readonly appointmentForm = this.forms.nonNullable.group({
    date: '',
    start: '',
    endDate: '',
    end: '',
    timezone: 'America/Toronto',
  });
  readonly statusForm = this.forms.nonNullable.group({
    status: '',
    date: '',
    time: '',
    timezone: this.browserTimezone,
    note: '',
  });

  private readonly destroy$ = new Subject<void>();
  private loadSequence = 0;
  private destroyed = false;
  private confirmingLeave = false;
  private returnFocus: HTMLElement | null = null;
  private target: WorkOrderPanelTarget = { tab: 'overview' };
  private focusTimer?: ReturnType<typeof setTimeout>;

  constructor(
    private readonly forms: FormBuilder,
    private readonly panelService: WorkOrderPanelService,
    private readonly workOrders: WorkOrdersV2Service,
    private readonly dialog: MatDialog,
    private readonly router: Router,
  ) {}

  get orderLabel(): string {
    return this.row?.order_label || this.details?.lading?.alternate_reference || 'Work order';
  }

  get currentStatus(): string {
    return this.details?.lading?.lading_status || this.row?.lading_status || 'Not set';
  }

  get saving(): boolean {
    return this.savingSection !== null || !!this.purchaseOrders?.saving;
  }

  get noteChanged(): boolean {
    return this.noteDraft !== (this.editingNote?.note || '');
  }

  get hasUnsavedChanges(): boolean {
    return (
      this.referenceForm.dirty ||
      this.appointmentForm.dirty ||
      this.freightForm.dirty ||
      this.statusForm.dirty ||
      this.noteChanged ||
      this.hasStopDrafts || !!this.purchaseOrders?.hasChanges
    );
  }

  get hasStopDrafts(): boolean {
    return this.stopEditors?.some((editor) => editor.hasChanges) || false;
  }

  get hasUnknownEquipment(): boolean {
    const value = this.freightForm.controls.type_of_equipment.value;
    return !!value && !this.equipmentTypes.some((equipment) => equipment.code === value);
  }

  get routeLabel(): string {
    if (!this.details) return this.row?.route_label || '';
    const lading = this.details.lading;
    const pickup = [lading.shipper_locality, lading.shipper_region].filter(Boolean).join(', ');
    const delivery = [lading.consignee_locality, lading.consignee_region].filter(Boolean).join(', ');
    return [pickup, delivery].filter(Boolean).join(' → ') || this.row?.route_label || '';
  }

  get appointmentError(): string | null {
    return appointmentValidation(this.appointmentForm.getRawValue());
  }

  get statusError(): string | null {
    return statusValidation(this.statusForm.getRawValue());
  }

  get canSaveNote(): boolean {
    const note = this.noteDraft.trim();
    return this.noteChanged && note.length > 0 && note !== '0';
  }

  async open(row: WorkOrderV2Row, target: WorkOrderPanelTarget = { tab: 'overview' }): Promise<void> {
    if (this.row?.lading_uuid === row.lading_uuid) {
      this.selectTarget(target);
      return;
    }
    if (!(await this.canLeave())) return;
    if (this.destroyed) return;

    this.returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.row = row;
    this.selectTarget(target);
    this.details = null;
    this.notes = [];
    this.statusHistory = [];
    this.notice = null;
    this.resetDrafts();
    void this.loadOrder();
  }

  async close(): Promise<void> {
    if (!(await this.canLeave())) return;
    this.loadSequence += 1;
    this.row = null;
    this.details = null;
    this.resetDrafts();
    this.closed.emit();
    if (this.returnFocus?.isConnected) this.returnFocus.focus();
  }

  async canLeave(): Promise<boolean> {
    if (this.saving || this.confirmingLeave) return false;
    if (!this.hasUnsavedChanges) return true;

    this.confirmingLeave = true;
    try {
      return await this.confirmDiscard(
        'There are unsaved changes to this work order. Discard them and continue?',
      );
    } finally {
      this.confirmingLeave = false;
    }
  }

  openFullDetails(tab?: string): void {
    if (!this.row || this.saving) return;
    // The route guard handles all unsaved sections, including drafts on hidden tabs.
    void this.router.navigate(['/work_order_details', this.row.lading_uuid], {
      queryParams: tab ? { tab } : undefined,
    });
  }

  selectTab(tab: WorkOrderPanelTab): void {
    this.selectTarget({ tab });
  }

  sectionHasDraft(tab: WorkOrderPanelTab): boolean {
    switch (tab) {
      case 'route':
        return this.hasStopDrafts;
      case 'overview':
        return this.referenceForm.dirty || !!this.purchaseOrders?.hasChanges;
      case 'schedule':
        return this.appointmentForm.dirty;
      case 'freight':
        return this.freightForm.dirty;
      case 'notes':
        return this.noteChanged;
      case 'status':
        return this.statusForm.dirty;
      default:
        return false;
    }
  }

  private selectTarget(target: WorkOrderPanelTarget): void {
    this.target = target;
    this.activeTab = target.tab;
    this.focusTarget();
  }

  private focusTarget(): void {
    clearTimeout(this.focusTimer);
    // Angular must reveal the target section before scrolling or focusing it.
    this.focusTimer = setTimeout(() => {
      const body = this.panelBody?.nativeElement;
      if (!body || !this.row || this.destroyed) return;
      body.scrollTop = 0;
      const field = this.loading
        ? undefined
        : Array.from(body.querySelectorAll<HTMLElement>('[data-panel-field]')).find(
            (element) => element.dataset['panelField'] === this.target.field,
          );
      if (field && !field.closest('[hidden]')) {
        field.focus({ preventScroll: true });
        field.scrollIntoView({ block: 'nearest', behavior: 'auto' });
      } else {
        body.focus({ preventScroll: true });
      }
    });
  }

  private applyDetails(details: PanelDetails): void {
    this.details = details;
    this.routeContacts = [panelContact(details, 'shipper'), panelContact(details, 'consignee')];
    this.partyContacts = [panelContact(details, 'customer'), panelContact(details, 'carrier')];
    this.freightSummary = freightFacts(details.lading);
    this.plannedTimes = scheduleFacts(details);
  }

  onStopSaving(role: WorkOrderStopRole, saving: boolean): void {
    if (saving) this.beginSave(role);
    else if (this.savingSection === role) this.savingSection = null;
  }

  onStopSaved(details: PanelDetails, role: WorkOrderStopRole): void {
    this.applyDetails(details);
    // A changed delivery location can change the default appointment timezone.
    // Preserve any appointment the user is already editing.
    if (this.appointmentForm.pristine) this.appointmentForm.reset(appointmentValues(details));
    this.savedSection(role, `${role === 'shipper' ? 'Shipper' : 'Consignee'} saved.`);
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (!this.row || (!this.hasUnsavedChanges && !this.saving)) return;
    event.preventDefault();
    event.returnValue = '';
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event: KeyboardEvent): void {
    if (!this.row || this.dialog.openDialogs.length > 0) return;
    const focusedInPanel = this.closeButton?.nativeElement
      .closest('.wo-panel')
      ?.contains(document.activeElement);
    if (focusedInPanel) {
      event.preventDefault();
      void this.close();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.loadSequence += 1;
    this.destroy$.next();
    this.destroy$.complete();
    clearTimeout(this.focusTimer);
  }

  async loadOrder(): Promise<void> {
    if (!this.row) return;
    const ladingUuid = this.row.lading_uuid;
    const sequence = ++this.loadSequence;
    this.loading = true;
    this.loadError = '';

    try {
      const details = await firstValueFrom(this.panelService.load(ladingUuid).pipe(takeUntil(this.destroy$)));
      if (sequence !== this.loadSequence) return;
      this.applyDetails(details);
      this.referenceForm.reset(referenceValues(details.lading));
      this.freightForm.reset(freightValues(details.lading));
      this.appointmentForm.reset(appointmentValues(details));
      this.notes = [...(details.lading_notes || [])];
      this.statusHistory = [...(details.status_timeline || [])];
      this.resetStatusDraft();
    } catch (error) {
      if (sequence === this.loadSequence && !this.destroyed) {
        this.loadError = this.errorMessage(error, 'Unable to load this work order.');
      }
    } finally {
      if (sequence === this.loadSequence) {
        this.loading = false;
        this.focusTarget();
      }
    }
  }

  onPurchaseOrdersSaved(): void { this.savedSection('references', 'Purchase orders saved in AMS.'); }

  async saveReferences(): Promise<void> {
    if (!this.details || !this.row || this.saving) return;
    const patch = referencePatch(this.referenceForm.getRawValue(), referenceValues(this.details.lading));
    if (Object.keys(patch).length === 0) {
      this.referenceForm.markAsPristine();
      return;
    }

    this.beginSave('references');
    try {
      const details = await firstValueFrom(this.panelService.saveFields(this.row.lading_uuid, patch));
      this.applyDetails(details);
      // Reset only this editor: appointment, note, and status drafts stay intact.
      this.referenceForm.reset(referenceValues(details.lading));
      this.savedSection('references', 'Order references saved.');
    } catch (error) {
      this.failedSection('references', error);
    } finally {
      this.savingSection = null;
    }
  }

  onAppointmentDateChange(): void {
    const values = this.appointmentForm.getRawValue();
    const previousDate = this.details ? appointmentValues(this.details).date : '';
    if (!values.endDate || values.endDate === previousDate) {
      this.appointmentForm.controls.endDate.setValue(values.date);
    }
  }

  clearAppointment(): void {
    if (this.saving) return;
    this.appointmentForm.patchValue({ date: '', start: '', endDate: '', end: '' });
    this.appointmentForm.markAsDirty();
  }

  async saveAppointment(): Promise<void> {
    if (!this.details || !this.row || this.saving || this.appointmentError) return;
    this.beginSave('appointment');

    try {
      const patch = appointmentPatch(this.appointmentForm.getRawValue());
      const details = await firstValueFrom(this.panelService.saveFields(this.row.lading_uuid, patch));
      this.applyDetails(details);
      this.appointmentForm.reset(appointmentValues(details));
      this.savedSection('appointment', 'Appointment window saved.');
    } catch (error) {
      this.failedSection('appointment', error);
    } finally {
      this.savingSection = null;
    }
  }

  async saveFreight(): Promise<void> {
    if (!this.details || !this.row || this.saving) return;
    const patch = freightPatch(this.freightForm.getRawValue(), freightValues(this.details.lading));
    if (Object.keys(patch).length === 0) {
      this.freightForm.markAsPristine();
      return;
    }

    this.beginSave('freight');
    try {
      const details = await firstValueFrom(this.panelService.saveFields(this.row.lading_uuid, patch));
      this.applyDetails(details);
      this.freightForm.reset(freightValues(details.lading));
      this.savedSection('freight', 'Freight and equipment saved.');
    } catch (error) {
      this.failedSection('freight', error);
    } finally {
      this.savingSection = null;
    }
  }

  async editNote(note?: LadingNote): Promise<void> {
    if (this.saving) return;
    if (this.noteChanged && !(await this.confirmDiscard('Discard the unsaved note and continue?'))) return;
    this.editingNote = note;
    this.noteDraft = note?.note || '';
    this.notice = null;
  }

  async saveNote(): Promise<void> {
    if (!this.row || this.saving || !this.canSaveNote) return;
    this.beginSave('notes');
    try {
      this.notes = await firstValueFrom(
        this.panelService.saveNote(this.row.lading_uuid, this.noteDraft, this.editingNote),
      );
      this.noteDraft = '';
      this.editingNote = undefined;
      this.savedSection('notes', 'Note saved.');
    } catch (error) {
      this.failedSection('notes', error);
    } finally {
      this.savingSection = null;
    }
  }

  async saveStatus(): Promise<void> {
    if (!this.row || this.saving || this.statusError) return;
    const values = this.statusForm.getRawValue();
    if (!this.statuses.includes(values.status)) return;
    const payload: WorkOrderV2StatusPayload = {
      lading_uuid: this.row.lading_uuid,
      lading_status: values.status,
      event_date: values.date,
      event_time: values.time,
      event_timezone: values.timezone,
      override_local_time: false,
      lading_status_note: values.note.trim() || null,
    };
    this.beginSave('status');

    try {
      const markProducts = await this.shouldMarkProductsDelivered(payload);
      const response = await firstValueFrom(this.workOrders.addStatus(payload));
      this.requireSuccess(response, 'The status could not be saved.');

      if (this.details) this.details.lading.lading_status = values.status;
      this.resetStatusDraft();
      this.savedSection('status', 'Status saved.');

      // A product update can fail after the status commits. Keep the status saved
      // and report a partial result instead of leaving a draft that could duplicate it.
      if (markProducts) await this.markProductsDelivered(payload.lading_uuid, response.data);
      await this.refreshStatusHistory(payload.lading_uuid);
    } catch (error) {
      this.failedSection('status', error);
    } finally {
      this.savingSection = null;
    }
  }

  formatTimestamp(value: string | null | undefined): string {
    if (!value) return '';
    const parsed = DateTime.fromSQL(value.replace('T', ' '));
    return parsed.isValid ? parsed.toFormat('MMM d, yyyy · h:mm a') : value;
  }

  private async shouldMarkProductsDelivered(payload: WorkOrderV2StatusPayload): Promise<boolean> {
    if (payload.lading_status !== 'Delivered Consignee') return false;
    const preview = await firstValueFrom(this.workOrders.getProductDeliveryPreview(payload.lading_uuid));
    this.requireSuccess(preview, 'Could not check product delivery timestamps. The status was not saved.');
    const count = Number(preview.missing_delivery_count || 0);
    if (count <= 0) return false;

    return firstValueFrom(
      this.dialog
        .open(ConfirmDialogComponent, {
          disableClose: true,
          data: {
            title: 'Mark products delivered?',
            message: `${count} products have no delivery time. Apply this delivery event time to them? Existing delivery times will stay as they are.`,
            confirmText: 'Mark delivered',
            cancelText: 'Status only',
            tone: 'warning',
          },
        })
        .afterClosed(),
    );
  }

  private async markProductsDelivered(
    ladingUuid: string,
    event?: WorkOrderV2StatusTimelineInfo,
  ): Promise<void> {
    try {
      if (!event?.local_time || !event.time_zone_id)
        throw new Error('The delivery event time was not returned.');
      const local = DateTime.fromSQL(event.local_time, { zone: event.time_zone_id });
      if (!local.isValid) throw new Error('The delivery event time could not be converted.');
      const response = await firstValueFrom(
        this.workOrders.markProductsDelivered(ladingUuid, local.toUTC().toFormat('yyyy-MM-dd HH:mm:ss')),
      );
      this.requireSuccess(response, 'Product delivery times could not be updated.');
    } catch (error) {
      this.notice = {
        section: 'status',
        tone: 'warning',
        message: `Status saved, but product delivery times need attention. ${this.errorMessage(error, '')}`,
      };
    }
  }

  private async refreshStatusHistory(ladingUuid: string): Promise<void> {
    try {
      const response = await firstValueFrom(this.workOrders.loadStatusTimeline(ladingUuid));
      this.requireSuccess(response, 'Could not refresh status history.');
      this.statusHistory = response.data || [];
    } catch {
      if (this.notice?.tone !== 'warning') {
        this.notice = {
          section: 'status',
          tone: 'warning',
          message: 'Status saved. Reopen the panel to refresh its history.',
        };
      }
    }
  }

  private beginSave(section: WorkOrderPanelSection): void {
    this.savingSection = section;
    this.notice = null;
  }

  private savedSection(section: WorkOrderPanelSection, message: string): void {
    this.notice = { section, tone: 'success', message };
    if (this.row) this.saved.emit({ ladingUuid: this.row.lading_uuid, section });
  }

  private failedSection(section: WorkOrderPanelSection, error: unknown): void {
    this.notice = {
      section,
      tone: 'error',
      message: this.errorMessage(error, 'Unable to save. Your changes are still here.'),
    };
  }

  private errorMessage(error: unknown, fallback: string): string {
    return error instanceof Error && error.message ? error.message : fallback;
  }

  private requireSuccess(
    response: { success?: boolean; code?: string | number; message?: string },
    fallback: string,
  ): void {
    if (response?.success === false || (response?.success !== true && String(response?.code) !== '200')) {
      throw new Error(response?.message || fallback);
    }
  }

  private confirmDiscard(message: string): Promise<boolean> {
    return firstValueFrom(
      this.dialog
        .open(ConfirmDialogComponent, {
          disableClose: true,
          data: {
            title: 'Unsaved changes',
            message,
            confirmText: 'Discard changes',
            cancelText: 'Keep editing',
            tone: 'warning',
          },
        })
        .afterClosed(),
    ).then((result) => result === true);
  }

  private resetDrafts(): void {
    this.stopEditors?.forEach((editor) => editor.discard());
    this.referenceForm.reset();
    this.freightForm.reset();
    this.appointmentForm.reset();
    this.noteDraft = '';
    this.editingNote = undefined;
    this.resetStatusDraft();
  }

  private resetStatusDraft(): void {
    const now = DateTime.fromJSDate(new Date()).setZone(this.browserTimezone);
    this.statusForm.reset({
      status: '',
      date: now.toFormat('yyyy-MM-dd'),
      time: now.toFormat('HH:mm'),
      timezone: this.browserTimezone,
      note: '',
    });
  }
}
